import type { FormInstance } from '@arco-design/web-react/es/Form';

import { ensureImageRegistryOnForm, readImageSourceUrl } from '@/delivery-type-plugin/capabilities/image';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  readModelInstanceFormValues,
  resolveModelInstancePdSeparation,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import {
  GATEWAY_NODES_FIELD,
  INFERENCE_SYSTEM_FIELD,
  MASTER_NODES_FIELD,
  WORKER_NODES_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { hasMaasInferenceSystemImage } from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';
import { SKU_RESOURCE_MODULES_FIELD } from '@/delivery-type-plugin/platform/scene-layout/types';

import { isMiGatewayModuleEnabled } from '../gatewayModule';
import { isMiKvOffloadModuleEnabled } from '../kvOffloadModule';

/**
 * 会承载推理/训练镜像的模块。
 * MI SKU 勾选列表不含 systemConfig；不得用 shouldValidateSkuModule(systemConfig)
 *（缺 key 视为需校验）误拦「仅勾模型与存储」等场景。
 */
const MI_IMAGE_CONFIRM_MODULE_KEYS = ['nodeGroups', 'gatewayConfig', 'kvOffload', 'systemConfig'] as const;

/** 无勾选态则仍确认；有勾选态时仅当镜像相关模块显式 true */
export function shouldConfirmModelInstanceImageOnForm(form: Pick<FormInstance, 'getFieldValue'>): boolean {
  const modules = form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as Record<string, boolean> | undefined;
  if (!modules || Object.keys(modules).length === 0) {
    return true;
  }
  return MI_IMAGE_CONFIRM_MODULE_KEYS.some((key) => modules[key] === true);
}

function readModelInstanceFrameworkImageUrl(values: Record<string, unknown>): string {
  if (hasMaasInferenceSystemImage(values as Parameters<typeof hasMaasInferenceSystemImage>[0])) {
    const inferenceSystem = parseFormJsonField<Record<string, unknown>>(values[INFERENCE_SYSTEM_FIELD] ?? values._inferenceSystem);
    return String(inferenceSystem?.image ?? inferenceSystem?.url ?? inferenceSystem?.imageKey ?? '').trim();
  }
  const frameworkImageRaw = parseFormJsonField<Record<string, unknown>>(values.inferenceFrameworkImage) ?? values.inferenceFrameworkImage;
  if (frameworkImageRaw && typeof frameworkImageRaw === 'object' && !Array.isArray(frameworkImageRaw)) {
    const frameworkImage = frameworkImageRaw as Record<string, unknown>;
    return String(frameworkImage.url ?? frameworkImage.image ?? frameworkImage.imageKey ?? '').trim();
  }
  return '';
}

function readEmbeddedNodeImageUrl(values: Record<string, unknown>): string {
  const pdSeparation = resolveModelInstancePdSeparation(values as Parameters<typeof resolveModelInstancePdSeparation>[0]);
  const inferenceNodes = pdSeparation
    ? [
        ...parseNodesField(values.prefillMasterNodes),
        ...parseNodesField(values.prefillWorkerNodes),
        ...parseNodesField(values.decodeMasterNodes),
        ...parseNodesField(values.decodeWorkerNodes),
      ]
    : [
        ...parseNodesField(values[MASTER_NODES_FIELD] ?? values.masterNodes),
        ...parseNodesField(values[WORKER_NODES_FIELD] ?? values.workerNodes),
      ];
  const nodes = [
    ...inferenceNodes,
    ...(isMiGatewayModuleEnabled(values as Parameters<typeof isMiGatewayModuleEnabled>[0])
      ? parseNodesField(values[GATEWAY_NODES_FIELD] ?? values.gatewayNodes)
      : []),
    ...(isMiKvOffloadModuleEnabled(values as Parameters<typeof isMiKvOffloadModuleEnabled>[0])
      ? [...parseNodesField(values.kvOffloadMasterNodes), ...parseNodesField(values.kvOffloadWorkerNodes)]
      : []),
  ];
  for (const node of nodes) {
    const system = node?.system as Record<string, unknown> | undefined;
    const image = String(system?.image ?? system?.url ?? '').trim();
    if (image) return image;
  }
  return '';
}

/** 模型实例 / 训练集群提交：probe 并补全 system.registry；未勾选镜像相关模块时跳过 */
export async function confirmModelInstanceImageOnForm(
  form: FormInstance,
  t: (key: string, options?: Record<string, unknown>) => string,
  options?: { emptySourceMessage?: string; /** 资源配置步「下一步」：尚未选镜像时不 probe */ allowEmptySource?: boolean },
) {
  if (!shouldConfirmModelInstanceImageOnForm(form)) {
    return { ok: true as const };
  }
  const values = readModelInstanceFormValues(form) as Record<string, unknown>;
  const system =
    parseFormJsonField<Record<string, unknown>>(form.getFieldValue('system')) ?? (values.system as Record<string, unknown> | undefined);
  const sourceUrl =
    readModelInstanceFrameworkImageUrl(values) ||
    readImageSourceUrl(values, ['system.image', 'system.url']) ||
    String(system?.image ?? system?.url ?? '').trim() ||
    readEmbeddedNodeImageUrl(values);
  if (!sourceUrl.trim() && options?.allowEmptySource) {
    return { ok: true as const };
  }
  return ensureImageRegistryOnForm(form, {
    sourceUrl,
    sourceUrlField: 'system.image',
    emptySourceMessage: options?.emptySourceMessage || t('data-center:maas.inference.image.required'),
    t,
  });
}
